import { useLang } from "../../../i18n/LangContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { Bilingual, emptyL } from "../Bilingual";

const outlineBtn = "h-8 border-white/15 bg-transparent text-slate-200 hover:bg-white/5 hover:text-white text-xs";

function ChecklistEditor({ item, index, onPatch }) {
  const { t } = useLang();
  return (
    <div className="space-y-2">
      {item.items.map((it, ii) => (
        <Bilingual key={`${item.content_id}-item-${ii}`} label={`${t("edu.type_CHECKLIST")} ${ii + 1}`}
          testId={`cms-checklist-${index}-${ii}`} value={it}
          onChange={(v) => onPatch({ items: item.items.map((x, y) => (y === ii ? v : x)) })} />
      ))}
      <Button type="button" variant="outline" data-testid={`cms-checklist-add-${index}`}
        onClick={() => onPatch({ items: [...item.items, { ...emptyL }] })} className={outlineBtn}>
        <Plus className="h-3 w-3 mr-1" /> {t("edu.addItem")}
      </Button>
    </div>
  );
}

function QuizEditor({ item, index, onPatch }) {
  const { t } = useLang();
  const patchQuestion = (qi, patch) =>
    onPatch({ questions: item.questions.map((x, y) => (y === qi ? { ...x, ...patch } : x)) });
  return (
    <div className="space-y-3">
      {item.questions.map((q, qi) => (
        <div key={`${item.content_id}-q-${qi}`} className="space-y-2 rounded-md border border-white/5 p-3">
          <Bilingual label={t("education.quizQuestion")} testId={`cms-quiz-q-${index}-${qi}`} value={q.question}
            onChange={(v) => patchQuestion(qi, { question: v })} />
          {q.options.map((opt, oi) => (
            <div key={`${item.content_id}-q-${qi}-o-${oi}`} className="flex items-start gap-2">
              <input type="radio" data-testid={`cms-quiz-correct-${index}-${qi}-${oi}`} checked={q.correct_index === oi}
                onChange={() => patchQuestion(qi, { correct_index: oi })} className="accent-[#10B981] mt-6" />
              <div className="flex-1">
                <Bilingual label={`${t("education.quizOption")} ${oi + 1}`} testId={`cms-quiz-opt-${index}-${qi}-${oi}`}
                  value={opt} onChange={(v) => patchQuestion(qi, { options: q.options.map((o, z) => (z === oi ? v : o)) })} />
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" data-testid={`cms-quiz-add-option-${index}-${qi}`}
            onClick={() => patchQuestion(qi, { options: [...q.options, { ...emptyL }] })} className={outlineBtn}>
            <Plus className="h-3 w-3 mr-1" /> {t("education.quizOption")}
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" data-testid={`cms-quiz-add-question-${index}`}
        onClick={() => onPatch({ questions: [...item.questions, { question: { ...emptyL }, options: [{ ...emptyL }, { ...emptyL }], correct_index: 0 }] })}
        className={outlineBtn}>
        <Plus className="h-3 w-3 mr-1" /> {t("education.addQuestion")}
      </Button>
    </div>
  );
}

export function ContentItemEditor({ item, index, onPatch, onMove, onRemove }) {
  const { t } = useLang();
  return (
    <div data-testid={`cms-content-${index}`} className="rounded-lg border border-white/10 bg-[#161923] p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{item.type}</span>
        <div className="flex gap-1">
          <Button type="button" size="icon" variant="ghost" data-testid={`cms-content-up-${index}`} onClick={() => onMove(-1)} className="h-7 w-7 text-slate-400 hover:bg-white/5"><ArrowUp className="h-3.5 w-3.5" /></Button>
          <Button type="button" size="icon" variant="ghost" data-testid={`cms-content-down-${index}`} onClick={() => onMove(1)} className="h-7 w-7 text-slate-400 hover:bg-white/5"><ArrowDown className="h-3.5 w-3.5" /></Button>
          <Button type="button" size="icon" variant="ghost" data-testid={`cms-content-remove-${index}`} onClick={onRemove} className="h-7 w-7 text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      <Bilingual label={t("education.moduleTitle")} testId={`cms-content-title-${index}`} value={item.title} onChange={(v) => onPatch({ title: v })} />

      {["VIDEO", "IMAGE", "PDF"].includes(item.type) && (
        <Input data-testid={`cms-content-url-${index}`} value={item.url || ""} onChange={(e) => onPatch({ url: e.target.value })}
          placeholder="https://..." className="bg-[#1C202E] border-white/10 text-white text-sm" />
      )}
      {["TEXT", "REFLECTION", "IMAGE", "PDF"].includes(item.type) && (
        <Bilingual label={t("education.content")} testId={`cms-content-body-${index}`} rows={4} value={item.body} onChange={(v) => onPatch({ body: v })} />
      )}
      {item.type === "CHECKLIST" && <ChecklistEditor item={item} index={index} onPatch={onPatch} />}
      {item.type === "QUIZ" && <QuizEditor item={item} index={index} onPatch={onPatch} />}
    </div>
  );
}
